import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { onAuthStateChanged, signOut, type User } from 'firebase/auth';
import { get, ref } from 'firebase/database';
import AppHeader from '../components/AppHeader';
import AddNoteModal from '../components/AddNoteModal';
import AttachmentViewerModal from '../components/AttachmentViewerModal';
import { usePetSummary, type PetNoteAttachment } from '../store/PetStore';
import { auth, database } from '../services/firebase';
import { Activity, AlertTriangle, Cpu, Download, PillBottle, Plus, Printer, Stethoscope, Syringe, Utensils } from 'lucide-react';
import { getMicrochipCompanyById } from '../constants/microchipCompanies';

function formatDate(value?: string) {
  if (!value) {
    return undefined;
  }
  try {
    return new Intl.DateTimeFormat(undefined, {
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
    }).format(new Date(value));
  } catch {
    return value;
  }
}

function formatTimestamp(value: number | string) {
  if (!value) {
    return 'Unknown date';
  }

  const parsed = new Date(value);
  if (isNaN(parsed.getTime())) {
    return 'Unknown date';
  }

  try {
    return new Intl.DateTimeFormat(undefined, {
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    }).format(parsed);
  } catch {
    return parsed.toLocaleString();
  }
}

function formatDateFromTimestamp(value?: number) {
  if (!value) {
    return undefined;
  }
  try {
    return new Intl.DateTimeFormat(undefined, {
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
    }).format(new Date(value));
  } catch {
    return undefined;
  }
}

type UserProfile = {
  organizationId?: string;
  needsOrganizationSetup?: boolean;
};

type FirebasePetPictureRecord = {
  url?: string;
  createdAt?: number;
  isCover?: boolean;
};

export default function PetSummary() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);

  const {
    pet,
    notes,
    medications,
    foods,
    allergies,
    vaccinations,
    conditions,
    status,
    errorMessage,
    loadSummary,
    reset,
    addExternalNote,
  } = usePetSummary();

  const [isDarkMode, setIsDarkMode] = useState(() => {
    if (typeof window === 'undefined') {
      return false;
    }
    return localStorage.getItem('theme') === 'dark';
  });

  useEffect(() => {
    const summaryToken = token;
    if (!summaryToken) {
      reset();
      return;
    }

    void loadSummary(summaryToken);
  }, [token, loadSummary, reset]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, user => {
      setCurrentUser(user);
    });
    return unsubscribe;
  }, []);

  useEffect(() => {
    if (!currentUser) {
      setProfile(null);
      return;
    }

    let isActive = true;
    const profileRef = ref(database, `users/${currentUser.uid}`);

    void get(profileRef)
      .then(snapshot => {
        if (!isActive) {
          return;
        }
        const value = snapshot.val() as UserProfile | null;
        setProfile(value);
      })
      .catch(error => {
        if (!isActive) {
          return;
        }
        console.warn('Failed to load profile for pet summary', error);
        setProfile(null);
      });

    return () => {
      isActive = false;
    };
  }, [currentUser]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    const storedTheme = localStorage.getItem('theme');
    if (storedTheme === 'dark') {
      setIsDarkMode(true);
    }
    if (storedTheme === 'light') {
      setIsDarkMode(false);
    }
  }, []);

  const handleThemeToggle = () => {
    setIsDarkMode(prev => {
      const next = !prev;
      if (typeof window !== 'undefined') {
        localStorage.setItem('theme', next ? 'dark' : 'light');
      }
      return next;
    });
  };

  const handleSignIn = () => {
    navigate('/signin', {
      state: token
        ? {
          returnTo: `/summary?${searchParams.toString()}`,
        }
        : undefined,
    });
  };

  const handleCreateAccount = () => {
    navigate('/register', {
      state: token
        ? {
          returnTo: `/summary?${searchParams.toString()}`,
        }
        : undefined,
    });
  };
  const summaryReturnPath = token ? `/summary?${searchParams.toString()}` : undefined;

  const handleAddExternalNote = useCallback(
    async ({
      authorName,
      content,
      context,
      attachments,
    }: {
      authorName: string;
      content: string;
      context?: string;
      attachments?: File[];
    }) => {
      if (!pet) {
        console.warn('Cannot save an external note without an active pet context.');
        return;
      }

      try {
        await addExternalNote(pet.id, {
          content,
          context,
          authorName: authorName.trim() || undefined,
          authorUserId: currentUser?.uid ?? undefined,
          attachments,
        });
      } catch (error) {
        console.error('Failed to save external note', error);
      }
    },
    [addExternalNote, currentUser, pet],
  );

  const handleLogout = () => {
    void signOut(auth)
      .then(() => {
        navigate('/signin', {
          replace: true,
          state: summaryReturnPath ? { returnTo: summaryReturnPath } : undefined,
        });
      })
      .catch(error => {
        console.warn('Failed to sign out', error);
      });
  };

  const hasNotes = useMemo(() => notes.length > 0, [notes]);
  const hasConditions = useMemo(() => conditions.length > 0, [conditions]);
  const hasFoods = useMemo(() => foods.length > 0, [foods]);
  const hasMedications = useMemo(() => medications.length > 0, [medications]);
  const hasAllergies = useMemo(() => allergies.length > 0, [allergies]);
  const hasVaccinations = useMemo(() => vaccinations.length > 0, [vaccinations]);
  const hasClinicalSections = hasConditions || hasAllergies || hasFoods || hasMedications || hasVaccinations;
  const trackerAllCategory = 'All';
  const trackerCategories = useMemo(() => {
    const unique = new Set<string>();
    notes.forEach(note => {
      if (note.category) {
        unique.add(note.category);
      }
    });
    if (unique.size === 0) {
      return [];
    }
    return [trackerAllCategory, ...Array.from(unique)];
  }, [notes]);
  const [selectedTrackerCategory, setSelectedTrackerCategory] = useState<string>(trackerAllCategory);
  const filteredNotes = useMemo(
    () =>
      selectedTrackerCategory === trackerAllCategory
        ? notes
        : notes.filter(note => note.category === selectedTrackerCategory),
    [notes, selectedTrackerCategory, trackerAllCategory],
  );
  const [generatedTimestamp] = useState(() => Date.now());
  const generatedTimestampLabel = formatTimestamp(generatedTimestamp);
  const [isAddNoteModalOpen, setIsAddNoteModalOpen] = useState(false);
  const [coverPhotoUri, setCoverPhotoUri] = useState<string | undefined>(() => pet?.photoUri);
  const [attachmentViewerState, setAttachmentViewerState] = useState<{
    noteId: string;
    attachments: PetNoteAttachment[];
    index: number;
    noteCreatedAt: number;
    noteCategory?: string;
  } | null>(null);
  const petId = pet?.id;
  const petPhotoUri = pet?.photoUri;
  const currentUserDisplayName = currentUser?.displayName ?? currentUser?.email ?? undefined;
  const hasOrganizationAccess = Boolean(profile?.organizationId);
  const isLoggedIn = Boolean(currentUser);
  const isVetSubscribed = Boolean(profile?.organizationId);
  const isVetOnFreePlan = isLoggedIn && !isVetSubscribed && Boolean(profile?.needsOrganizationSetup);
  const isPetOwnerUser = isLoggedIn && !isVetSubscribed && !isVetOnFreePlan;
  const showAddNoteButton = isPetOwnerUser || isVetOnFreePlan || isVetSubscribed;
  const showDownloadButton = isVetOnFreePlan;
  const showImportButton = isVetSubscribed;
  const showSectionQuickActions = isVetSubscribed;

  const handleOpenAttachmentViewer = useCallback(
    (noteId: string, attachments: PetNoteAttachment[], index: number, noteCreatedAt: number, noteCategory?: string) => {
      if (!attachments.length) {
        return;
      }
      setAttachmentViewerState({
        noteId,
        attachments,
        index: Math.max(0, Math.min(index, attachments.length - 1)),
        noteCreatedAt,
        noteCategory,
      });
    },
    [],
  );

  const handleCloseAttachmentViewer = useCallback(() => {
    setAttachmentViewerState(null);
  }, []);

  const handleSelectAttachmentFromStrip = useCallback((index: number) => {
    setAttachmentViewerState(prev => {
      if (!prev || index < 0 || index >= prev.attachments.length) {
        return prev;
      }
      return { ...prev, index };
    });
  }, []);

  const handleGoToNextAttachment = useCallback(() => {
    setAttachmentViewerState(prev => {
      if (!prev || prev.attachments.length <= 1) {
        return prev;
      }
      const nextIndex = (prev.index + 1) % prev.attachments.length;
      return { ...prev, index: nextIndex };
    });
  }, []);

  const handleGoToPreviousAttachment = useCallback(() => {
    setAttachmentViewerState(prev => {
      if (!prev || prev.attachments.length <= 1) {
        return prev;
      }
      const nextIndex = (prev.index - 1 + prev.attachments.length) % prev.attachments.length;
      return { ...prev, index: nextIndex };
    });
  }, []);

  useEffect(() => {
    if (!petId) {
      setCoverPhotoUri(undefined);
      return;
    }

    let isActive = true;
    setCoverPhotoUri(petPhotoUri);

    const picturesRef = ref(database, `petPictures/${petId}`);

    void get(picturesRef)
      .then(snapshot => {
        if (!isActive) {
          return;
        }

        const data = snapshot.val() as Record<string, FirebasePetPictureRecord> | null;
        if (!data) {
          setCoverPhotoUri(petPhotoUri);
          return;
        }

        const pictures = Object.values(data)
          .map(entry => ({
            url: typeof entry.url === 'string' ? entry.url : '',
            createdAt: typeof entry.createdAt === 'number' ? entry.createdAt : 0,
            isCover: entry.isCover === true,
          }))
          .filter(entry => entry.url.length > 0)
          .sort((a, b) => b.createdAt - a.createdAt);

        const cover = pictures.find(entry => entry.isCover);
        const fallback = pictures[0];
        setCoverPhotoUri(cover?.url ?? fallback?.url ?? petPhotoUri);
      })
      .catch(error => {
        if (!isActive) {
          return;
        }
        console.warn('Failed to load pet pictures for summary', error);
        setCoverPhotoUri(petPhotoUri);
      });

    return () => {
      isActive = false;
    };
  }, [petId, petPhotoUri]);

  useEffect(() => {
    if (!attachmentViewerState) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        handleCloseAttachmentViewer();
      } else if (event.key === 'ArrowRight') {
        handleGoToNextAttachment();
      } else if (event.key === 'ArrowLeft') {
        handleGoToPreviousAttachment();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [attachmentViewerState, handleCloseAttachmentViewer, handleGoToNextAttachment, handleGoToPreviousAttachment]);

  useEffect(() => {
    if (selectedTrackerCategory === trackerAllCategory) {
      return;
    }
    if (!trackerCategories.includes(selectedTrackerCategory)) {
      setSelectedTrackerCategory(trackerAllCategory);
    }
  }, [selectedTrackerCategory, trackerCategories, trackerAllCategory]);

  const pageWrapperClass = `${isDarkMode ? 'bg-slate-900 text-slate-100' : 'bg-white text-slate-900'} min-h-screen pb-16 print-page`;
  const detailCardBaseClass = `flex-1 p-4 shadow-sm ring-1 ${isDarkMode ? 'bg-slate-800 ring-slate-700' : 'bg-slate-100 ring-slate-200'} print-card`;
  const pillClass = `inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold tracking-wide ${isDarkMode ? 'bg-slate-200 text-slate-900' : 'bg-slate-900 text-white'}`;
  const timestampClass = `text-xs font-medium uppercase tracking-wide ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`;
  const sectionSubtitleClass = `text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`;
  const countBadgeClass = `rounded-full px-3 py-1 text-xs font-semibold ${isDarkMode ? 'bg-slate-800 text-slate-200' : 'bg-slate-100 text-slate-600'}`;
  const detailLabelClass = `text-xs font-regular uppercase tracking-wide ${isDarkMode ? 'text-slate-400' : 'text-slate-400'}`;
  const detailValueClass = `text-base font-semibold ${isDarkMode ? 'text-slate-100' : 'text-slate-800'}`;
  const ownerNoteTextClass = `text-base leading-relaxed whitespace-pre-line ${isDarkMode ? 'text-slate-200' : 'text-slate-700'}`;
  const primaryActionButtonClass = `inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold shadow-sm transition ${isDarkMode ? 'bg-blue-500 text-white hover:bg-blue-600' : 'bg-blue-600 text-white hover:bg-blue-700'}`;
  const secondaryActionButtonClass = `inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-semibold transition ${isDarkMode ? 'border-slate-600 bg-slate-800 text-slate-100 hover:bg-slate-700' : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-100'}`;
  const sectionHeaderActionsClass = 'flex items-center gap-2';
  const quickAddButtonClass = `inline-flex h-9 w-9 items-center justify-center rounded-full text-white shadow-sm transition focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-2 ${isDarkMode ? 'bg-blue-500 hover:bg-blue-600 focus:ring-offset-slate-900' : 'bg-blue-600 hover:bg-blue-700 focus:ring-offset-white'}`;
  const generatedLabelClass = `text-xs font-medium ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`;
  const innerContainerClass = `mx-auto w-full max-w-6xl py-6 px-4 sm:px-6 lg:px-8`;
  const clinicalSectionContainerClass = `mx-auto w-full max-w-6xl ${hasClinicalSections ? 'py-6' : 'py-1'} px-4 sm:px-6 lg:px-8`;
  const sharedSummarySectionClass = `${isDarkMode ? 'bg-slate-800/50 text-slate-100' : 'bg-slate-50 text-slate-900'} w-full py-0 print-surface`;
  const defaultSectionClass = `${isDarkMode ? 'bg-slate-900 text-slate-100' : 'bg-white text-slate-900'} w-full py-0 print-surface`;
  const healthOverviewSectionClass = `${sharedSummarySectionClass} border-y ${isDarkMode ? 'border-slate-800' : 'border-slate-200'}`;
  const sectionHeaderClass = 'flex flex-wrap items-center justify-between gap-3';
  const sectionHeaderTitleClass = `flex items-center gap-2 text-lg font-semibold ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`;
  const sectionCardsRowClass = 'grid gap-8 sm:grid-cols-2';
  const sectionCardItemClass = '';
  const trackerTableClass = `overflow-hidden rounded-2xl ring-1 shadow-sm ${isDarkMode ? 'bg-slate-900 ring-slate-700' : 'bg-white ring-slate-200'} print-card`;
  const trackerHeaderClass = `hidden grid-cols-[1.05fr_0.9fr_0.95fr_1.6fr_0.9fr] items-center gap-4 px-4 py-3 text-[11px] font-semibold uppercase tracking-wide ${isDarkMode ? 'md:bg-slate-800 md:text-slate-300' : 'md:bg-slate-50 md:text-slate-600'} md:grid`;
  const trackerRowListClass = `divide-y ${isDarkMode ? 'divide-slate-800' : 'divide-slate-200'}`;
  const trackerRowClass = `grid grid-cols-1 gap-3 px-4 py-4 transition md:grid-cols-[1.05fr_0.9fr_0.95fr_1.6fr_0.9fr] md:gap-4 ${isDarkMode ? 'hover:bg-slate-800/70' : 'hover:bg-slate-50'}`;
  const trackerMobileLabelClass = `text-[11px] font-semibold uppercase tracking-wide ${isDarkMode ? 'text-slate-400' : 'text-slate-500'} md:hidden`;
  const trackerValueEmphasisClass = `text-base font-semibold ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`;
  const trackerMetaTextClass = `text-sm leading-snug ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`;
  const trackerFilterRowClass = 'flex w-full flex-wrap items-center gap-2';
  const trackerFilterButtonClass = `inline-flex items-center rounded-full border px-3 py-1 text-sm font-semibold transition ${isDarkMode ? 'border-slate-700 text-slate-200 hover:border-blue-400 hover:text-blue-200' : 'border-slate-200 text-slate-700 hover:border-blue-500 hover:text-blue-700'}`;
  const trackerFilterButtonActiveClass = `${isDarkMode ? 'bg-blue-600 text-white border-blue-600' : 'bg-blue-600 text-white border-blue-600'}`;
  const trackerAttachmentStripClass = `md:col-span-5 -mx-2 mt-1 rounded-xl px-2 pb-1 pt-2 ${isDarkMode ? 'bg-slate-900/60' : 'bg-slate-50'}`;
  const trackerAttachmentLabelClass = `hidden text-[11px] font-semibold uppercase tracking-wide ${isDarkMode ? 'text-slate-400' : 'text-slate-500'} md:block`;
  const trackerAttachmentThumbButtonClass = `relative h-16 w-16 flex-shrink-0 overflow-hidden rounded-lg ring-1 transition focus:outline-none focus:ring-2 ${isDarkMode ? 'ring-slate-700 bg-slate-800/80 hover:ring-blue-300 focus:ring-blue-400' : 'ring-slate-200 bg-white hover:ring-blue-500 focus:ring-blue-600'} shadow-sm`;
  const trackerAttachmentThumbImageClass = 'h-full w-full object-cover';
  const conditionCardClass = `rounded-lg border p-5 shadow-sm ${isDarkMode ? 'border-slate-700 bg-slate-900' : 'border-slate-200 bg-white'} print-card`;
  const standardCardClass = conditionCardClass;
  const cardTitleClass = `text-base font-semibold ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`;
  const cardMetaClass = `text-sm ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`;
  const conditionCategoryClass = `text-xs font-semibold uppercase tracking-wide ${isDarkMode ? 'text-blue-200' : 'text-blue-600'}`;
  const tagsRowClass = 'flex flex-wrap gap-2';
  const tagPillClass = `inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${isDarkMode ? 'bg-slate-800 text-slate-200' : 'bg-slate-200 text-slate-700'}`;
  const emptyCollectionClass = `flex min-h-[60px] flex-col items-center justify-center rounded-lg border border-dashed text-center ${isDarkMode ? 'border-slate-700 text-slate-400' : 'border-slate-300 text-slate-500'} print-card`;
  const printStyles = `
    @media print {
      body {
        background: #fff !important;
        color: #000 !important;
      }
      .print-hide,
      header {
        display: none !important;
      }
      .print-page,
      .print-surface,
      .print-card {
        background: #fff !important;
        color: #000 !important;
      }
      .print-card {
        box-shadow: none !important;
        border-color: #000 !important;
      }
    }
  `;

  const renderCenteredMessage = (title: string, message: ReactNode) => (
    <div className={pageWrapperClass}>
      <AppHeader
        isDarkMode={isDarkMode}
        onToggleTheme={handleThemeToggle}
        currentUser={currentUser}
        onSignIn={handleSignIn}
        onCreateAccount={handleCreateAccount}
        onLogout={handleLogout}
        showDashboardLink={hasOrganizationAccess}
      />
      <main>
        <section className={defaultSectionClass}>
          <div className={`${innerContainerClass} flex min-h-[360px] flex-col items-center justify-center`}>
            <div className={`${isDarkMode ? 'border-slate-700 bg-slate-900/40 text-slate-100 shadow-slate-900/40' : 'border-slate-300 bg-white text-slate-700 shadow-slate-200/70'} w-full max-w-2xl rounded-3xl border border-dashed px-8 py-16 text-center shadow-sm`}>
              <h1 className="text-2xl font-semibold">{title}</h1>
              <div className="mt-4 text-base leading-relaxed">{message}</div>
            </div>
          </div>
        </section>
      </main>
    </div>
  );

  if (!token) {
    return renderCenteredMessage('Pet Summary', (
      <span>
        No token was provided. Append <code className="rounded bg-slate-200 px-1 py-0.5 text-xs font-semibold text-slate-700">?token=YOUR_TOKEN</code> to the URL to view a pet.
      </span>
    ));
  }

  if (status === 'loading' || status === 'idle') {
    return renderCenteredMessage('Pet Summary', 'Loading pet data…');
  }

  if (status === 'error') {
    return renderCenteredMessage('Pet Summary', `Unable to load pet data. ${errorMessage ?? ''}`);
  }

  if (!pet) {
    return renderCenteredMessage('Pet Summary', 'Pet data is unavailable.');
  }

  const birthDateLabel = formatDate(pet.birthDate);

  const adoptionDateLabel = formatDate(pet.adoptionDate);
  const ownerNote = pet.notes?.trim();

  const detailEntries: Array<{ label: string; value?: string }> = [];

  // Nickname first (if present)
  if (pet.nickname) detailEntries.push({ label: 'Nickname', value: pet.nickname });

  // Always show Species next
  detailEntries.push({ label: 'Species', value: pet.species });

  // Then the rest (if present)
  if (pet.gender) detailEntries.push({ label: 'Gender', value: pet.gender });
  if (pet.breed) detailEntries.push({ label: 'Breed', value: pet.breed });
  if (birthDateLabel) detailEntries.push({ label: 'Birth Date', value: birthDateLabel });
  if (adoptionDateLabel) detailEntries.push({ label: 'Adoption Date', value: adoptionDateLabel });

  const displayPhotoUri = coverPhotoUri ?? pet.photoUri;
  const attachmentViewerTimestampLabel = attachmentViewerState
    ? formatTimestamp(attachmentViewerState.noteCreatedAt)
    : undefined;
  const isAttachmentViewerOpen =
    !!attachmentViewerState &&
    attachmentViewerState.attachments.length > 0 &&
    attachmentViewerState.index >= 0 &&
    attachmentViewerState.index < attachmentViewerState.attachments.length;
  const detailCardClassName = `${detailCardBaseClass} ring-inset rounded-2xl`;
  const stackedCardClassName = `${detailCardBaseClass} ring-inset rounded-2xl`;
  const ownerNoteCardClass = `${detailCardBaseClass} ring-inset rounded-2xl mt-4`;
  const hasPhoto = Boolean(displayPhotoUri);
  const microchipCompany = getMicrochipCompanyById(pet.microchipCompanyId);
  const hasMicrochipDetails = Boolean(pet.microchipSerial || microchipCompany);

  return (
    <div className={pageWrapperClass}>
      <style dangerouslySetInnerHTML={{ __html: printStyles }} />
      <div className="print-hide">
        <AppHeader
          isDarkMode={isDarkMode}
          onToggleTheme={handleThemeToggle}
          currentUser={currentUser}
          onSignIn={handleSignIn}
          onCreateAccount={handleCreateAccount}
          onLogout={handleLogout}
          showDashboardLink={hasOrganizationAccess}
        />
      </div>
      <main className="flex flex-col gap-0">
        <section className={defaultSectionClass}>
          <div className={`${innerContainerClass} flex flex-col gap-6`}>
            <div className="space-y-2 print-hide">
              <p className={`${sectionSubtitleClass} font-semibold uppercase tracking-wide`}>
                Shared Summary
              </p>
              <h1 className={`text-3xl font-bold sm:text-4xl ${isDarkMode ? 'text-slate-50' : 'text-slate-900'}`}>
                Health Summary for {pet.name}
              </h1>
            </div>

            <div className="flex flex-col">
              <div className={`grid gap-5 lg:items-start ${hasPhoto ? 'lg:grid-cols-2' : ''}`}>
                <div className="flex flex-col gap-4">
                  <section aria-labelledby="pet-details" className={detailCardClassName}>
                    <dl className="space-y-1">
                      {detailEntries.map(entry => (
                        <div key={entry.label} className="grid grid-cols-[max-content_1fr] items-center gap-3 py-1">
                          <dt className={`${detailLabelClass} text-sm`}>{entry.label}</dt>
                          <dd className={`${detailValueClass} text-right`}>{entry.value ?? '—'}</dd>
                        </div>
                      ))}
                    </dl>
                  </section>

                  {hasMicrochipDetails && (
                    <section aria-labelledby="pet-microchip" className={stackedCardClassName}>
                      <div className="flex items-center gap-2">
                        <span className={`${isDarkMode ? 'text-blue-300' : 'text-blue-600'}`}>
                          <Cpu className="h-5 w-5" aria-hidden="true" />
                        </span>
                        <h2 id="pet-microchip" className={`text-lg font-semibold ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>
                          Microchip
                        </h2>
                      </div>
                      <dl className="mt-2 space-y-1">
                        {pet.microchipSerial ? (
                          <div className="grid grid-cols-[max-content_1fr] items-center gap-3 py-1">
                            <dt className={`${detailLabelClass} text-sm`}>Chip #</dt>
                            <dd className={`${detailValueClass} text-right`}>{pet.microchipSerial}</dd>
                          </div>
                        ) : null}
                        {microchipCompany ? (
                          <div className="grid grid-cols-[max-content_1fr] items-center gap-3 py-1">
                            <dt className={`${detailLabelClass} text-sm`}>Company</dt>
                            <dd className={`${detailValueClass} text-right`}>{microchipCompany.name}</dd>
                          </div>
                        ) : null}
                        {microchipCompany?.phone ? (
                          <div className="grid grid-cols-[max-content_1fr] items-center gap-3 py-1">
                            <dt className={`${detailLabelClass} text-sm`}>Hotline</dt>
                            <dd className={`${detailValueClass} text-right`}>
                              <a
                                className={`${isDarkMode ? 'text-blue-300 hover:text-blue-200' : 'text-blue-600 hover:text-blue-700'}`}
                                href={`tel:${microchipCompany.phone.replace(/[^0-9+]/g, '')}`}
                              >
                                {microchipCompany.phone}
                              </a>
                            </dd>
                          </div>
                        ) : null}
                        {microchipCompany?.website ? (
                          <div className="grid grid-cols-[max-content_1fr] items-center gap-3 py-1">
                            <dt className={`${detailLabelClass} text-sm`}>Website</dt>
                            <dd className={`${detailValueClass} text-right`}>
                              <a
                                className={`${isDarkMode ? 'text-blue-300 hover:text-blue-200' : 'text-blue-600 hover:text-blue-700'}`}
                                href={microchipCompany.website}
                                target="_blank"
                                rel="noreferrer"
                              >
                                {microchipCompany.website.replace(/^https?:\/\//i, '')}
                              </a>
                            </dd>
                          </div>
                        ) : null}
                      </dl>
                    </section>
                  )}
                </div>

                {hasPhoto ? (
                  <div className="flex items-start justify-end">
                    <div
                      className={`relative aspect-square w-full max-w-[420px] overflow-hidden rounded-3xl ring-1 ${isDarkMode ? 'ring-slate-700 bg-slate-800/80' : 'ring-slate-200 bg-slate-50'} print-card`}
                    >
                      <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 via-transparent to-transparent" aria-hidden="true" />
                      <img
                        src={displayPhotoUri}
                        alt={`${pet.name} portrait`}
                        className="absolute inset-0 h-full w-full object-cover"
                      />
                    </div>
                  </div>
                ) : null}
              </div>

              {ownerNote && (
                <section className={ownerNoteCardClass}>
                  <p className={ownerNoteTextClass}>{ownerNote}</p>
                </section>
              )}
            </div>

            <div className="grid gap-8 lg:grid-cols-[minmax(0,1.2fr)_minmax(0,0.8fr)]">
              <div className="space-y-4">
                <div className="flex flex-wrap items-center gap-3 print-hide">
                  {showAddNoteButton ? (
                    <button
                      type="button"
                      className={primaryActionButtonClass}
                      onClick={() => setIsAddNoteModalOpen(true)}
                    >
                      <Plus className="h-4 w-4" aria-hidden="true" />
                      Add Note
                    </button>
                  ) : null}
                  {showDownloadButton ? (
                    <button type="button" className={secondaryActionButtonClass}>
                      <Download className="h-4 w-4" aria-hidden="true" />
                      Download
                    </button>
                  ) : null}
                  {showImportButton ? (
                    <button type="button" className={secondaryActionButtonClass}>
                      <Download className="h-4 w-4" aria-hidden="true" />
                      Import
                    </button>
                  ) : null}
                  <button
                    type="button"
                    className={secondaryActionButtonClass}
                    onClick={() => {
                      if (typeof window !== 'undefined') {
                        window.print();
                      }
                    }}
                  >
                    <Printer className="h-4 w-4" aria-hidden="true" />
                    Print
                  </button>
                </div>

                <p className={generatedLabelClass}>Generated {generatedTimestampLabel}</p>
              </div>
            </div>
          </div>
        </section>

        <section className={healthOverviewSectionClass} aria-labelledby="pet-health-summary">
          <div className={`${clinicalSectionContainerClass} flex flex-col gap-4`}>
            <h2 id="pet-health-summary" className="sr-only">Pet Health Summary</h2>

            {hasConditions ? (
              <section className="space-y-4" aria-labelledby="pet-conditions">
                <div className={sectionHeaderClass}>
                  <div className={sectionHeaderTitleClass}>
                    <span className={`${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`}>
                      <Stethoscope className="h-5 w-5" aria-hidden="true" />
                    </span>
                    <span id="pet-conditions">Health Conditions</span>
                    {conditions.length > 0 && (
                      <span className={countBadgeClass}>
                        {conditions.length} item{conditions.length === 1 ? '' : 's'}
                      </span>
                    )}
                  </div>
                  <div className={sectionHeaderActionsClass}>
                    {showSectionQuickActions ? (
                      <button
                        type="button"
                        className={`${quickAddButtonClass} print-hide`}
                        aria-label="Add health condition"
                      >
                        <Plus className="h-4 w-4" aria-hidden="true" />
                      </button>
                    ) : null}
                  </div>
                </div>

                <div className={sectionCardsRowClass}>
                  {conditions.map(condition => {
                    const diagnosedLabel = formatDateFromTimestamp(condition.diagnosedAt);
                    const tags = condition.tags ?? [];
                    return (
                      <div key={condition.id} className={`${sectionCardItemClass} ${conditionCardClass}`}>
                        <div className="space-y-3">
                          <div className="flex flex-col gap-1">
                            {condition.category && (
                              <span className={conditionCategoryClass}>{condition.category}</span>
                            )}
                            <p className={cardTitleClass}>{condition.name}</p>
                          </div>
                          <div className="space-y-2">
                            <p className={cardMetaClass}>{condition.active ? 'Active' : 'Inactive'}</p>
                            {diagnosedLabel && (
                              <p className={cardMetaClass}>Diagnosed: {diagnosedLabel}</p>
                            )}
                          </div>
                          {tags.length > 0 && (
                            <div className={tagsRowClass}>
                              {tags.map(tag => (
                                <span key={tag} className={tagPillClass}>
                                  {tag}
                                </span>
                              ))}
                            </div>
                          )}
                          {condition.notes && (
                            <p className={cardMetaClass}>{condition.notes}</p>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </section>
            ) : null}

            {hasAllergies ? (
              <section className="space-y-4" aria-labelledby="pet-allergies">
                <div className={sectionHeaderClass}>
                  <div className={sectionHeaderTitleClass}>
                    <span className={`${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`}>
                      <AlertTriangle className="h-5 w-5" aria-hidden="true" />
                    </span>
                    <span id="pet-allergies">Allergies</span>
                    {allergies.length > 0 && (
                      <span className={countBadgeClass}>
                        {allergies.length} item{allergies.length === 1 ? '' : 's'}
                      </span>
                    )}
                  </div>
                  <div className={sectionHeaderActionsClass}>
                    {showSectionQuickActions ? (
                      <button
                        type="button"
                        className={`${quickAddButtonClass} print-hide`}
                        aria-label="Add allergy"
                      >
                        <Plus className="h-4 w-4" aria-hidden="true" />
                      </button>
                    ) : null}
                  </div>
                </div>

                <div className={sectionCardsRowClass}>
                  {allergies.map(allergy => (
                    <div key={allergy.id} className={`${sectionCardItemClass} ${standardCardClass}`}>
                      <div className="space-y-2">
                        <p className={cardTitleClass}>{allergy.name}</p>
                        {allergy.reaction && (
                          <p className={cardMetaClass}>
                            Reaction: {allergy.reaction}
                          </p>
                        )}
                        {allergy.notes && <p className={cardMetaClass}>{allergy.notes}</p>}
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            ) : null}

            {hasFoods ? (
              <section className="space-y-4" aria-labelledby="pet-foods">
                <div className={sectionHeaderClass}>
                  <div className={sectionHeaderTitleClass}>
                    <span className={`${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`}>
                      <Utensils className="h-5 w-5" aria-hidden="true" />
                    </span>
                    <span id="pet-foods">Food</span>
                    {foods.length > 0 && (
                      <span className={countBadgeClass}>
                        {foods.length} item{foods.length === 1 ? '' : 's'}
                      </span>
                    )}
                  </div>
                  <div className={sectionHeaderActionsClass}>
                    {showSectionQuickActions ? (
                      <button
                        type="button"
                        className={`${quickAddButtonClass} print-hide`}
                        aria-label="Add food"
                      >
                        <Plus className="h-4 w-4" aria-hidden="true" />
                      </button>
                    ) : null}
                  </div>
                </div>

                <div className={sectionCardsRowClass}>
                  {foods.map(food => {
                    const summaryParts = [
                      [food.amount, food.unit].filter(Boolean).join(' ').trim(),
                      food.kind,
                      food.frequency,
                    ].filter(Boolean);
                    return (
                      <div key={food.id} className={`${sectionCardItemClass} ${standardCardClass}`}>
                        <div className="space-y-2">
                          <p className={cardTitleClass}>{food.brand}</p>
                          {summaryParts.length > 0 && (
                            <p className={cardMetaClass}>{summaryParts.join(', ')}</p>
                          )}
                          {food.notes && <p className={cardMetaClass}>{food.notes}</p>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </section>
            ) : null}

            {hasMedications ? (
              <section className="space-y-4" aria-labelledby="pet-medications">
                <div className={sectionHeaderClass}>
                  <div className={sectionHeaderTitleClass}>
                    <span className={`${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`}>
                      <PillBottle className="h-5 w-5" aria-hidden="true" />
                    </span>
                    <span id="pet-medications">Medications</span>
                    {medications.length > 0 && (
                      <span className={countBadgeClass}>
                        {medications.length} item{medications.length === 1 ? '' : 's'}
                      </span>
                    )}
                  </div>
                  <div className={sectionHeaderActionsClass}>
                    {showSectionQuickActions ? (
                      <button
                        type="button"
                        className={`${quickAddButtonClass} print-hide`}
                        aria-label="Add medication"
                      >
                        <Plus className="h-4 w-4" aria-hidden="true" />
                      </button>
                    ) : null}
                  </div>
                </div>

                <div className={sectionCardsRowClass}>
                  {medications.map(medication => {
                    const unitSizeLabel = medication.unitSize
                      ? [medication.unitSize, medication.unit].filter(Boolean).join(' ')
                      : '';
                    const dosageLabel = medication.dosage?.trim() ?? '';
                    const summaryLine = [
                      dosageLabel && unitSizeLabel
                        ? `${dosageLabel} x ${unitSizeLabel}`
                        : dosageLabel || unitSizeLabel,
                      medication.form,
                      medication.frequency,
                    ]
                      .filter(Boolean)
                      .join(', ');
                    const prescribedLabel = medication.prescribedOn
                      ? formatDate(medication.prescribedOn)
                      : undefined;
                    return (
                      <div key={medication.id} className={`${sectionCardItemClass} ${standardCardClass}`}>
                        <div className="space-y-2">
                          <p className={cardTitleClass}>{medication.name}</p>
                          {summaryLine && <p className={cardMetaClass}>{summaryLine}</p>}
                          {prescribedLabel && (
                            <p className={cardMetaClass}>Prescribed: {prescribedLabel}</p>
                          )}
                          {medication.duration && <p className={cardMetaClass}>{medication.duration}</p>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </section>
            ) : null}

            {hasVaccinations ? (
              <section className="space-y-4" aria-labelledby="pet-vaccinations">
                <div className={sectionHeaderClass}>
                  <div className={sectionHeaderTitleClass}>
                    <span className={`${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`}>
                      <Syringe className="h-5 w-5" aria-hidden="true" />
                    </span>
                    <span id="pet-vaccinations">Vaccinations</span>
                    {vaccinations.length > 0 && (
                      <span className={countBadgeClass}>
                        {vaccinations.length} item{vaccinations.length === 1 ? '' : 's'}
                      </span>
                    )}
                  </div>
                  <div className={sectionHeaderActionsClass}>
                    {showSectionQuickActions ? (
                      <button
                        type="button"
                        className={`${quickAddButtonClass} print-hide`}
                        aria-label="Add vaccination"
                      >
                        <Plus className="h-4 w-4" aria-hidden="true" />
                      </button>
                    ) : null}
                  </div>
                </div>

                <div className={sectionCardsRowClass}>
                  {vaccinations.map(vaccination => {
                    const administeredLabel = formatDateFromTimestamp(vaccination.administeredOn);
                    return (
                      <div key={vaccination.id} className={`${sectionCardItemClass} ${standardCardClass}`}>
                        <div className="space-y-2">
                          <p className={cardTitleClass}>{vaccination.name}</p>
                          {administeredLabel && (
                            <p className={cardMetaClass}>Administered {administeredLabel}</p>
                          )}
                          {vaccination.notes && <p className={cardMetaClass}>{vaccination.notes}</p>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </section>
            ) : null}
          </div>
        </section>

        <section className={defaultSectionClass} aria-labelledby="pet-notes">
          <div className={`${innerContainerClass} space-y-4`}>
            <div className={sectionHeaderClass}>
              <div className={sectionHeaderTitleClass}>
                <span className={`${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`}>
                  <Activity className="h-5 w-5" aria-hidden="true" />
                </span>
                <span id="pet-notes">Health Tracker</span>
                {notes.length > 0 && (
                  <span className={countBadgeClass}>
                    {notes.length} item{notes.length === 1 ? '' : 's'}
                  </span>
                )}
              </div>
            </div>

            {trackerCategories.length > 0 ? (
              <div className="flex flex-col gap-2">
                <div className={`${trackerFilterRowClass} overflow-x-auto pb-1`}>
                  {trackerCategories.map(category => {
                    const selected = category === selectedTrackerCategory;
                    return (
                      <button
                        type="button"
                        key={category}
                        onClick={() => setSelectedTrackerCategory(category)}
                        className={`${trackerFilterButtonClass} ${selected ? trackerFilterButtonActiveClass : ''}`}
                      >
                        {category}
                      </button>
                    );
                  })}
                </div>
                <p className={`${sectionSubtitleClass} text-xs`}>
                  Showing {filteredNotes.length} of {notes.length} entries
                </p>
              </div>
            ) : null}

            {hasNotes ? (
              <div className={trackerTableClass}>
                <div className={trackerHeaderClass}>
                  <span>Measure</span>
                  <span>Value</span>
                  <span>State / Type / Severity</span>
                  <span>Context / Triggers / Notes</span>
                  <span className="text-right">Recorded</span>
                </div>
                <ul className={trackerRowListClass}>
                  {filteredNotes.map(note => {
                    const categoryKey = note.category?.toLowerCase() ?? '';
                    const isDiarrheaOrVomit =
                      categoryKey === 'diarrhea' ||
                      categoryKey === 'diarhhea' ||
                      categoryKey === 'vomit' ||
                      categoryKey === 'vomiting';
                    const measurement = note.value ? `${note.value}${note.unit ? ` ${note.unit}` : ''}` : '';
                    const valueLabel = isDiarrheaOrVomit
                      ? (note.type && note.type.trim() ? note.type : '—')
                      : measurement || '—';
                    const isCough =
                      categoryKey === 'cough' ||
                      note.incidentType?.toLowerCase?.() === 'cough';
                    const isSyncopeOrSeizure =
                      categoryKey === 'seizure' ||
                      categoryKey === 'syncope' ||
                      (note.incidentType?.toLowerCase?.() === 'seizure') ||
                      (note.incidentType?.toLowerCase?.() === 'syncope');
                    const defaultStateParts = [note.type, note.incidentType].filter(Boolean);
                    const defaultStateLabel = defaultStateParts.length > 0 ? defaultStateParts.join(' / ') : '—';
                    const amountSizeParts = [note.amount, note.size].filter(part => Boolean(part && String(part).trim()));
                    const stateLabel = isDiarrheaOrVomit
                      ? (amountSizeParts.length > 0 ? amountSizeParts.join(', ') : '—')
                      : isCough
                        ? (note.type && note.type.trim() ? note.type : '—')
                        : isSyncopeOrSeizure
                          ? (note.type && note.type.trim() ? note.type : '—')
                          : defaultStateLabel;
                    const attachments = Array.isArray(note.attachments) ? note.attachments : [];
                    const hasAttachments = attachments.length > 0;

                    return (
                      <li key={note.id} className={trackerRowClass}>
                        <div className="space-y-2">
                          <p className={trackerMobileLabelClass}>Measure</p>
                          <div className="flex flex-wrap items-center gap-2">
                            <span className={pillClass}>
                              {note.category ?? 'General'}
                            </span>
                          </div>
                        </div>

                        <div className="space-y-1">
                          <p className={trackerMobileLabelClass}>Value</p>
                          <p className={trackerValueEmphasisClass}>{valueLabel}</p>
                          {!isDiarrheaOrVomit && note.amount ? <p className={trackerMetaTextClass}>Amount: {note.amount}</p> : null}
                        </div>

                        <div className="space-y-1">
                          <p className={trackerMobileLabelClass}>State</p>
                          <p className={trackerMetaTextClass}>{stateLabel}</p>
                          {!isDiarrheaOrVomit && note.size ? <p className={trackerMetaTextClass}>Size: {note.size}</p> : null}
                        </div>

                        <div className="space-y-1">
                          <p className={trackerMobileLabelClass}>Notes / Context</p>
                          {note.context && (
                            <p className={trackerMetaTextClass}>
                              <span className="font-semibold">Context: </span>
                              {note.context}
                            </p>
                          )}
                          {note.triggers && note.triggers.length > 0 && (
                            <p className={trackerMetaTextClass}>
                              <span className="font-semibold">Triggers: </span>
                              {note.triggers.join(', ')}
                            </p>
                          )}
                          {note.text && (
                            <p className={`${trackerMetaTextClass} italic`}>
                              {note.text}
                            </p>
                          )}
                        </div>

                        <div className="space-y-1 md:text-right">
                          <p className={trackerMobileLabelClass}>Recorded</p>
                          <p className={`${timestampClass} text-left md:text-right`}>
                            {formatTimestamp(note.createdAt)}
                          </p>
                        </div>

                        {hasAttachments ? (
                          <div className={trackerAttachmentStripClass}>
                            <div className="mb-2 flex items-center gap-2">
                              <p className={`${trackerMobileLabelClass} block md:hidden`}>Attachments</p>
                              <p className={trackerAttachmentLabelClass}>Attachments</p>
                            </div>
                            <div className="flex items-center gap-3 overflow-x-auto pb-1 md:flex-wrap">
                              {attachments.map((attachment, index) => (
                                <button
                                  key={`${attachment.storagePath ?? attachment.url}-preview`}
                                  type="button"
                                  onClick={() =>
                                    handleOpenAttachmentViewer(
                                      note.id,
                                      attachments,
                                      index,
                                      note.createdAt,
                                      note.category,
                                    )
                                  }
                                  className={trackerAttachmentThumbButtonClass}
                                  aria-label={`Open attachment ${index + 1}`}
                                >
                                  <img
                                    src={attachment.url}
                                    alt={`${note.category ?? 'Health tracker'} attachment ${index + 1}`}
                                    className={trackerAttachmentThumbImageClass}
                                  />
                                </button>
                              ))}
                            </div>
                          </div>
                        ) : null}
                      </li>
                    );
                  })}
                </ul>
              </div>
            ) : (
              <div className={emptyCollectionClass}>
                <p className="text-sm font-medium">No notes are available for this pet yet.</p>
              </div>
            )}
          </div>
        </section>
      </main>
      <div className="print-hide">
        <AttachmentViewerModal
          isOpen={isAttachmentViewerOpen}
          attachments={attachmentViewerState?.attachments ?? []}
          activeIndex={attachmentViewerState?.index ?? 0}
          isDarkMode={isDarkMode}
          categoryLabel={attachmentViewerState?.noteCategory ?? 'Health Tracker'}
          timestampLabel={attachmentViewerTimestampLabel}
          onClose={handleCloseAttachmentViewer}
          onNext={handleGoToNextAttachment}
          onPrevious={handleGoToPreviousAttachment}
          onSelectIndex={handleSelectAttachmentFromStrip}
          timestampClassName={timestampClass}
        />
        <AddNoteModal
          isOpen={isAddNoteModalOpen}
          onClose={() => setIsAddNoteModalOpen(false)}
          onSave={handleAddExternalNote}
          currentUserName={currentUserDisplayName}
          isDarkMode={isDarkMode}
        />
      </div>
    </div>
  );
}
